import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Check, Mail, Search, Users, UsersRound, X } from "lucide-react";
import { CalendarDirectoryPerson } from "../../../interfaces/calendar/calendarDirectoryOptions";
import Team from "../../../interfaces/identity/Team";
import {
    excludeInviteesById,
    getTeamMembers,
    isEmployeeInvitee,
    isIncludedBySelectedTeam,
    normalizeExternalEmail,
    normalizeInviteeId,
    resolveInviteeIds,
} from "./participantInviteSelection";

export type InviteeFilter = "all" | "employees" | "participants" | "teams";

export interface ParticipantSelectionChange {
    participantUserIds: string[];
    individualUserIds: string[];
    selectedTeamIds: number[];
    externalUserEmails: string[];
}

interface ParticipantSelectDropdownProps {
    label?: string;
    name: string;
    participantUserIds: string[];
    individualUserIds?: string[];
    selectedTeamIds?: number[];
    externalUserEmails: string[];
    onChange: (data: ParticipantSelectionChange) => void;
    options: CalendarDirectoryPerson[];
    teams?: Team[];
    excludedUserIds?: string[];
    error?: string;
}

const FILTERS: Array<{ value: InviteeFilter; label: string }> = [
    { value: "all", label: "All" },
    { value: "employees", label: "Employees" },
    { value: "participants", label: "Participants" },
    { value: "teams", label: "Teams" },
];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ParticipantSelectDropdown: React.FC<ParticipantSelectDropdownProps> = ({
    label,
    participantUserIds,
    individualUserIds = participantUserIds,
    selectedTeamIds = [],
    externalUserEmails,
    onChange,
    options,
    teams = [],
    excludedUserIds,
    error,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeFilter, setActiveFilter] = useState<InviteeFilter>("all");
    const [coords, setCoords] = useState({
        top: 0,
        left: 0,
        width: 0,
        maxListHeight: 300,
        placement: "bottom" as "top" | "bottom",
    });

    const triggerRef = useRef<HTMLDivElement>(null);
    const portalRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const listboxId = useId();
    const labelId = useId();

    const normalizedSearch = searchTerm.trim().toLowerCase();
    const selectableOptions = useMemo(
        () => excludeInviteesById(options, excludedUserIds ?? []),
        [excludedUserIds, options],
    );
    const internalEmails = useMemo(
        () => new Set(options.map((person) => normalizeExternalEmail(person.email ?? "")).filter(Boolean)),
        [options],
    );
    const normalizedExternalEmails = useMemo(
        () => new Set(externalUserEmails.map(normalizeExternalEmail)),
        [externalUserEmails],
    );

    const filteredOptions = useMemo(() => selectableOptions.filter((person) => {
        const matchesType = activeFilter === "all"
            || (activeFilter === "employees" && isEmployeeInvitee(person))
            || (activeFilter === "participants" && !isEmployeeInvitee(person));
        const matchesSearch = !normalizedSearch
            || person.userName?.toLowerCase().includes(normalizedSearch)
            || person.email?.toLowerCase().includes(normalizedSearch);
        return activeFilter !== "teams" && matchesType && matchesSearch;
    }), [activeFilter, normalizedSearch, selectableOptions]);

    const visibleTeams = useMemo(() => teams.filter((team) => {
        if (getTeamMembers(selectableOptions, team.id).length === 0) return false;
        return !normalizedSearch
            || team.teamName.toLowerCase().includes(normalizedSearch)
            || team.regionName?.toLowerCase().includes(normalizedSearch);
    }), [normalizedSearch, selectableOptions, teams]);

    const normalizedCandidateEmail = normalizeExternalEmail(searchTerm);
    const canAddEmail = emailRegex.test(normalizedCandidateEmail)
        && !normalizedExternalEmails.has(normalizedCandidateEmail)
        && !internalEmails.has(normalizedCandidateEmail);
    const duplicateInternalEmail = emailRegex.test(normalizedCandidateEmail)
        && internalEmails.has(normalizedCandidateEmail);

    const selectedUsers = useMemo(() => {
        const selectedIds = new Set(participantUserIds.map(normalizeInviteeId));
        return selectableOptions.filter((option) => selectedIds.has(normalizeInviteeId(option.userId)));
    }, [participantUserIds, selectableOptions]);

    const selectedTeams = useMemo(
        () => teams.filter((team) => selectedTeamIds.includes(team.id)),
        [selectedTeamIds, teams],
    );
    const usersWithoutEmailDetails = selectedUsers.filter((user) => !user.email?.trim());
    const selectedCount = participantUserIds.length + externalUserEmails.length;

    const openDropdown = () => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setCoords((previous) => ({
            ...previous,
            top: rect.bottom + 8,
            left: rect.left,
            width: rect.width,
        }));
        setIsOpen(true);
    };

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (portalRef.current?.contains(target)) return;
            if (triggerRef.current?.contains(target)) return;
            setIsOpen(false);
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
        }

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [isOpen]);

    useLayoutEffect(() => {
        if (!isOpen) return;

        const updatePosition = () => {
            const trigger = triggerRef.current;
            const popup = portalRef.current;
            const list = listRef.current;
            if (!trigger || !popup || !list) return;

            const viewport = window.visualViewport;
            const viewportWidth = viewport?.width ?? window.innerWidth;
            const viewportHeight = viewport?.height ?? window.innerHeight;
            const viewportOffsetLeft = viewport?.offsetLeft ?? 0;
            const viewportOffsetTop = viewport?.offsetTop ?? 0;
            const viewportPadding = 8;
            const gap = 8;
            const triggerRect = trigger.getBoundingClientRect();
            const spaceAbove = triggerRect.top - viewportOffsetTop - viewportPadding - gap;
            const spaceBelow = viewportOffsetTop + viewportHeight - triggerRect.bottom - viewportPadding - gap;
            const popupChromeHeight = Math.max(104, popup.offsetHeight - list.offsetHeight);
            const desiredListHeight = Math.min(300, Math.max(72, list.scrollHeight));
            const desiredPopupHeight = popupChromeHeight + desiredListHeight;
            const placement = spaceBelow < desiredPopupHeight && spaceAbove > spaceBelow
                ? "top"
                : "bottom";
            const availableSpace = Math.max(0, placement === "top" ? spaceAbove : spaceBelow);
            const maxListHeight = Math.max(72, Math.min(300, availableSpace - popupChromeHeight));
            const renderedPopupHeight = popupChromeHeight + Math.min(desiredListHeight, maxListHeight);
            const width = Math.min(triggerRect.width, viewportWidth - (viewportPadding * 2));
            const minLeft = viewportOffsetLeft + viewportPadding;
            const maxLeft = viewportOffsetLeft + viewportWidth - width - viewportPadding;
            const left = Math.min(Math.max(triggerRect.left, minLeft), maxLeft);
            const top = placement === "top"
                ? Math.max(viewportOffsetTop + viewportPadding, triggerRect.top - gap - renderedPopupHeight)
                : triggerRect.bottom + gap;

            setCoords({ top, left, width, maxListHeight, placement });
        };

        updatePosition();
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);
        window.visualViewport?.addEventListener("resize", updatePosition);
        window.visualViewport?.addEventListener("scroll", updatePosition);

        return () => {
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
            window.visualViewport?.removeEventListener("resize", updatePosition);
            window.visualViewport?.removeEventListener("scroll", updatePosition);
        };
    }, [activeFilter, isOpen, normalizedSearch, options.length, selectedCount, teams.length]);

    useEffect(() => {
        if (isOpen) inputRef.current?.focus();
        if (!isOpen) {
            setSearchTerm("");
            setActiveFilter("all");
        }
    }, [isOpen]);

    const emitChange = (
        nextIndividualUserIds: string[],
        nextSelectedTeamIds: number[],
        nextExternalEmails = externalUserEmails,
    ) => {
        const selectableIds = new Set(selectableOptions.map((person) => normalizeInviteeId(person.userId)));
        const sanitizedIndividualIds = nextIndividualUserIds.filter((id) => selectableIds.has(normalizeInviteeId(id)));
        onChange({
            participantUserIds: resolveInviteeIds(sanitizedIndividualIds, nextSelectedTeamIds, selectableOptions),
            individualUserIds: sanitizedIndividualIds,
            selectedTeamIds: nextSelectedTeamIds,
            externalUserEmails: nextExternalEmails,
        });
    };

    const toggleUser = (userId: string) => {
        const normalizedId = normalizeInviteeId(userId);
        const isIndividuallySelected = individualUserIds.some((id) => normalizeInviteeId(id) === normalizedId);
        const nextIndividualIds = isIndividuallySelected
            ? individualUserIds.filter((id) => normalizeInviteeId(id) !== normalizedId)
            : [...individualUserIds, userId];
        emitChange(nextIndividualIds, selectedTeamIds);
    };

    const toggleTeam = (teamId: number) => {
        const nextTeamIds = selectedTeamIds.includes(teamId)
            ? selectedTeamIds.filter((id) => id !== teamId)
            : [...selectedTeamIds, teamId];
        emitChange(individualUserIds, nextTeamIds);
    };

    const removeExternalEmail = (email: string) => {
        const normalizedEmail = normalizeExternalEmail(email);
        emitChange(individualUserIds, selectedTeamIds, externalUserEmails.filter((item) => normalizeExternalEmail(item) !== normalizedEmail));
    };

    const addExternalEmail = () => {
        if (!canAddEmail) return;
        emitChange(individualUserIds, selectedTeamIds, [...externalUserEmails, normalizedCandidateEmail]);
        setSearchTerm("");
    };

    const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (["Enter", " ", "ArrowDown"].includes(event.key)) {
            event.preventDefault();
            if (!isOpen) openDropdown();
        } else if (event.key === "Escape") {
            event.stopPropagation();
            setIsOpen(false);
        }
    };

    return (
        <div className="flex w-full flex-col gap-1.5">
            {label && <label id={labelId} className="ml-1 text-[11px] font-bold uppercase tracking-widest text-gray-500">{label}</label>}

            <div
                ref={triggerRef}
                role="combobox"
                tabIndex={0}
                aria-labelledby={label ? labelId : undefined}
                aria-label={label ? undefined : "Invite people"}
                aria-controls={listboxId}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                onKeyDown={handleTriggerKeyDown}
                onClick={() => isOpen ? setIsOpen(false) : openDropdown()}
                className={`relative flex min-h-[48px] cursor-pointer items-center rounded-xl border bg-gray-50/50 py-1.5 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-[#7E57C2]/10 dark:bg-[var(--sprout-input)]
                    ${error && error !== "SomeParticipantNoEmail" ? "border-red-300 bg-red-50/30" : isOpen ? "border-[#7E57C2] bg-white ring-4 ring-[#7E57C2]/10 dark:bg-[var(--sprout-input)]" : "border-gray-200 hover:border-gray-300 dark:border-[var(--sprout-input-border)]"}`}
            >
                <Users size={18} className={`absolute left-3.5 top-[15px] transition-colors ${isOpen ? "text-[#7E57C2]" : "text-gray-400"}`} />

                <div className="flex flex-1 flex-wrap items-center gap-1.5 pr-8" style={{ paddingLeft: "2.75rem" }}>
                    {selectedCount === 0 && <span className="select-none py-1.5 text-sm text-gray-400">Select people, teams, or type an email…</span>}

                    {selectedTeams.map((team) => {
                        const memberCount = getTeamMembers(selectableOptions, team.id).length;
                        return (
                            <span key={`team-${team.id}`} className="flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs font-bold text-indigo-700 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-300">
                                <UsersRound size={12} /> {team.teamName} · {memberCount}
                                <button type="button" aria-label={`Remove ${team.teamName}`} onClick={(event) => { event.stopPropagation(); toggleTeam(team.id); }} className="ml-0.5 rounded-full p-0.5 hover:bg-indigo-200 dark:hover:bg-indigo-500/20"><X size={12} /></button>
                            </span>
                        );
                    })}

                    {selectedUsers.filter((user) => !isIncludedBySelectedTeam(user, selectedTeamIds)).map((user) => (
                        <span key={user.userId} className="flex items-center gap-1 rounded-lg border border-purple-100 bg-purple-50 px-2 py-1 text-xs font-bold text-[#7E57C2] shadow-sm dark:border-purple-500/30 dark:bg-purple-500/10 dark:text-purple-300">
                            {user.userName}
                            <button type="button" aria-label={`Remove ${user.userName}`} onClick={(event) => { event.stopPropagation(); toggleUser(user.userId); }} className="ml-0.5 rounded-full p-0.5 hover:bg-purple-200 dark:hover:bg-purple-500/20"><X size={12} /></button>
                        </span>
                    ))}

                    {externalUserEmails.map((email) => (
                        <span key={email} className="flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-100 px-2 py-1 text-xs font-bold text-gray-600 shadow-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200">
                            <Mail size={12} className="mr-0.5 opacity-60" /> {email}
                            <button type="button" aria-label={`Remove ${email}`} onClick={(event) => { event.stopPropagation(); removeExternalEmail(email); }} className="ml-0.5 rounded-full p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600"><X size={12} /></button>
                        </span>
                    ))}
                </div>
            </div>

            {isOpen && ReactDOM.createPortal(
                <div ref={portalRef} data-testid="participant-select-popup" data-placement={coords.placement} className="participant-select-popup dark-popup fixed z-[999999] overflow-hidden rounded-xl border border-gray-100 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900" style={{ top: coords.top, left: coords.left, width: coords.width }}>
                    <div className="border-b border-gray-100 bg-gray-50/50 p-2 dark:border-gray-700 dark:bg-gray-900">
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input ref={inputRef} type="text" aria-label="Search invitees" placeholder={activeFilter === "teams" ? "Search teams…" : "Search by name or email…"} value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} onKeyDown={(event) => {
                                if (event.key === "Enter" && activeFilter !== "teams" && canAddEmail) { event.preventDefault(); addExternalEmail(); }
                                else if (event.key === "Escape") { event.stopPropagation(); setIsOpen(false); triggerRef.current?.focus(); }
                            }} className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-8 pr-4 text-sm outline-none transition-all focus:border-[#7E57C2] focus:ring-2 focus:ring-[#7E57C2]/10 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
                        </div>

                        <div role="tablist" aria-label="Filter invitees" className="mt-2 grid grid-cols-4 gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
                            {FILTERS.map((filter) => (
                                <button key={filter.value} type="button" role="tab" aria-selected={activeFilter === filter.value} onClick={() => setActiveFilter(filter.value)} className={`rounded-md px-1.5 py-1.5 text-[11px] font-bold transition-colors ${activeFilter === filter.value ? "bg-white text-[#7E57C2] shadow-sm dark:bg-gray-700 dark:text-purple-300" : "text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"}`}>{filter.label}</button>
                            ))}
                        </div>
                    </div>

                    <div ref={listRef} id={listboxId} role="listbox" aria-multiselectable="true" className="overflow-y-auto bg-white py-1 custom-scrollbar dark:bg-gray-900" style={{ maxHeight: coords.maxListHeight }}>
                        {activeFilter !== "teams" && canAddEmail && (
                            <button type="button" onClick={addExternalEmail} className="flex w-full items-center gap-3 border-b border-gray-100 px-4 py-3 text-left hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-50 dark:bg-indigo-500/10"><Mail size={14} className="text-indigo-500" /></span>
                                <span className="flex min-w-0 flex-1 flex-col"><span className="text-sm font-bold text-gray-800 dark:text-gray-100">Invite external guest</span><span className="truncate text-xs text-gray-500">{normalizedCandidateEmail}</span></span>
                            </button>
                        )}

                        {activeFilter !== "teams" && duplicateInternalEmail && <div className="px-4 py-2 text-xs font-medium text-amber-700 dark:text-amber-300">That email already belongs to a person in this directory.</div>}

                        {activeFilter === "teams" ? visibleTeams.map((team) => {
                            const members = getTeamMembers(selectableOptions, team.id);
                            const isSelected = selectedTeamIds.includes(team.id);
                            return (
                                <button key={team.id} type="button" role="option" aria-selected={isSelected} onClick={() => toggleTeam(team.id)} className="group flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-[#F9F6FF] dark:hover:bg-purple-500/10">
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-500 dark:bg-indigo-500/10 dark:text-indigo-300"><UsersRound size={16} /></span>
                                    <span className="flex min-w-0 flex-1 flex-col"><span className="truncate text-sm font-bold text-gray-800 group-hover:text-[#7E57C2] dark:text-gray-100">{team.teamName}</span><span className="truncate text-xs text-gray-400">Invite {members.length} employee{members.length === 1 ? "" : "s"}</span></span>
                                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${isSelected ? "border-[#7E57C2] bg-[#7E57C2]" : "border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800"}`}>{isSelected && <Check size={12} className="text-white" strokeWidth={3} />}</span>
                                </button>
                            );
                        }) : filteredOptions.map((person) => {
                            const personId = normalizeInviteeId(person.userId);
                            const isSelected = participantUserIds.some((id) => normalizeInviteeId(id) === personId);
                            const includedByTeam = isIncludedBySelectedTeam(person, selectedTeamIds);
                            const selectedTeamNames = teams.filter((team) => selectedTeamIds.includes(team.id) && person.teamIds?.includes(team.id)).map((team) => team.teamName);
                            return (
                                <button key={person.userId} type="button" role="option" aria-selected={isSelected} disabled={includedByTeam} onClick={() => toggleUser(person.userId)} className={`group flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${includedByTeam ? "cursor-default bg-gray-50/70 opacity-65 dark:bg-gray-800/40" : "hover:bg-[#F9F6FF] dark:hover:bg-purple-500/10"}`}>
                                    <span className="flex min-w-0 flex-1 flex-col"><span className="flex items-center gap-2"><span className="truncate text-sm font-bold text-gray-800 group-hover:text-[#7E57C2] dark:text-gray-100">{person.userName}</span><span className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider ${isEmployeeInvitee(person) ? "bg-indigo-50 text-indigo-500 dark:bg-indigo-500/10 dark:text-indigo-300" : "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300"}`}>{isEmployeeInvitee(person) ? "Employee" : "Participant"}</span></span><span className="mt-0.5 truncate text-xs text-gray-400">{includedByTeam ? `Included through ${selectedTeamNames.join(", ")}` : person.email || "No email address on file"}</span></span>
                                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${isSelected ? "border-[#7E57C2] bg-[#7E57C2]" : "border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800"}`}>{isSelected && <Check size={12} className="text-white" strokeWidth={3} />}</span>
                                </button>
                            );
                        })}

                        {((activeFilter === "teams" && visibleTeams.length === 0) || (activeFilter !== "teams" && filteredOptions.length === 0 && !canAddEmail && !duplicateInternalEmail)) && <div className="px-4 py-8 text-center text-sm italic text-gray-400">No matching {activeFilter === "teams" ? "teams" : "people"} found</div>}
                    </div>

                    {selectedCount > 0 && <div className="border-t border-gray-100 px-4 py-2 text-xs font-semibold text-gray-500 dark:border-gray-700 dark:text-gray-400">{selectedCount} attendee{selectedCount === 1 ? "" : "s"} selected</div>}
                </div>,
                document.body,
            )}

            {usersWithoutEmailDetails.length > 0 && (
                <div className="mt-1.5 rounded-xl border border-amber-100 bg-amber-50 p-2.5 shadow-sm dark:border-amber-500/30 dark:bg-amber-500/10">
                    <p className="m-0 text-[11px] leading-snug text-amber-800 dark:text-amber-200"><span className="mb-1 flex items-center gap-1.5 font-bold"><Mail size={12} /> Invitation warning</span>{usersWithoutEmailDetails.length} selected attendee{usersWithoutEmailDetails.length === 1 ? " does" : "s do"} not have an email address and will not receive a calendar invitation.</p>
                </div>
            )}

            {error && error !== "SomeParticipantNoEmail" && <div className="ml-1 mt-1.5 flex items-center gap-1"><span className="text-[11px] font-medium tracking-tight text-red-500">{error}</span></div>}
        </div>
    );
};

export default ParticipantSelectDropdown;
